import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useNotifications(filters?: {
	category?: string;
	search?: string;
	page?: number;
	limit?: number;
}) {
	return useQuery({
		queryKey: queryKeys.notifications.list(filters),
		queryFn: async () => {
			const response = await notificationsApi.getAll(filters);
			return (
				response.data || {
					notifications: [],
					total: 0,
					unread_count: 0,
					page: 1,
					has_more: false,
				}
			);
		},
		staleTime: 30_000, // 30s — avoid excessive refetches on page navigation
		refetchInterval: 60000, // Poll every 60s — same as unread count
		refetchIntervalInBackground: true, // Keep polling even when tab is not focused
		refetchOnWindowFocus: true,
	});
}

export function useUnreadCount() {
	return useQuery({
		queryKey: queryKeys.notifications.unreadCount(),
		queryFn: async () => {
			const response = await notificationsApi.getUnreadCount();
			return response.data?.count ?? 0;
		},
		refetchInterval: 60000,
		refetchIntervalInBackground: true, // Keep polling even when tab is not focused
		staleTime: 10000, // 10s — avoid duplicate fetches from sidebar + mobile header
		refetchOnWindowFocus: true,
	});
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useMarkAsRead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => notificationsApi.markAsRead(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
		},
	});
}

export function useMarkAllAsRead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (category?: string) =>
			notificationsApi.markAllAsRead(category),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
		},
	});
}

export function useDeleteNotification() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => notificationsApi.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
		},
	});
}

export function useDeleteNotificationsBulk() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (ids: string[]) => notificationsApi.deleteBulk(ids),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.unreadCount(),
			});
		},
	});
}
